import React, { useMemo } from 'react';
import { Wallet, TrendingUp, Clock, User, Home } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { useScreenInit } from '../../useScreenInit.js';
import { APARTMENTS } from './appartamentiData';
import { usePlatform, initials } from '../../context/PlatformContext';
interface EarningRow {
  apartmentName: string;
  cleaner: string;
  earning: number;
  date: string;
}
export function CmGuadagniPage() {
  useScreenInit();
  const {
    jobs
  } = usePlatform();
  const completed = useMemo<EarningRow[]>(() => {
    const rows: EarningRow[] = [];
    APARTMENTS.forEach(a => a.interventions.forEach(i => {
      if (i.status === 'Completata') {
        rows.push({
          apartmentName: a.name,
          cleaner: i.cleaner,
          earning: i.earning,
          date: i.date
        });
      }
    }));
    return rows;
  }, []);
  const incassato = completed.reduce((s, r) => s + r.earning, 0);
  const daIncassare = jobs.filter(j => j.status !== 'completata').reduce((s, j) => s + j.earning, 0);
  const payoutPerCleaner = useMemo(() => {
    const map: Record<string, number> = {};
    completed.forEach(r => {
      map[r.cleaner] = (map[r.cleaner] || 0) + r.earning;
    });
    return Object.entries(map).map(([cleaner, total]) => ({
      cleaner,
      total
    })).sort((a, b) => b.total - a.total);
  }, [completed]);
  const maxPayout = payoutPerCleaner[0]?.total ?? 1;
  return <MobileLayout data-id="element-1782">
      <div className="mb-6" data-id="element-1783">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-1784">Guadagni</h1>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-id="element-1785">
        <Card data-id="element-1786">
          <CardContent data-id="element-1787">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3" data-id="element-1788">
              <Wallet size={18} data-id="element-1789" />
            </div>
            <div className="text-2xl font-bold text-navy" data-id="element-1790">€{incassato}</div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-1791">
              Incassato
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-1792">
          <CardContent data-id="element-1793">
            <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center mb-3" data-id="element-1794">
              <Clock size={18} data-id="element-1795" />
            </div>
            <div className="text-2xl font-bold text-navy" data-id="element-1796">€{daIncassare}</div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-1797">
              Da incassare
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-1798">
          <CardContent data-id="element-1799">
            <div className="w-9 h-9 rounded-lg bg-coral/10 text-coral flex items-center justify-center mb-3" data-id="element-1800">
              <TrendingUp size={18} data-id="element-1801" />
            </div>
            <div className="text-2xl font-bold text-navy" data-id="element-1802">
              €{incassato + daIncassare}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5" data-id="element-1803">
              Totale stimato
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payout per cleaner */}
      <h2 className="text-sm font-semibold text-navy flex items-center gap-2 mb-3" data-id="element-1804">
        <User size={16} className="text-coral" data-id="element-1805" />
        Payout per cleaner
      </h2>
      <Card className="mb-6" data-id="element-1806">
        <CardContent className="space-y-4" data-id="element-1807">
          {payoutPerCleaner.map(p => <div key={p.cleaner} data-id="element-1808">
              <div className="flex items-center justify-between mb-1.5" data-id="element-1809">
                <span className="flex items-center gap-2 text-sm font-medium text-foreground" data-id="element-1810">
                  <span className="w-7 h-7 rounded-full bg-coral/10 flex items-center justify-center text-[10px] font-bold text-coral" data-id="element-1811">
                    {initials(p.cleaner)}
                  </span>
                  {p.cleaner}
                </span>
                <span className="text-sm font-semibold text-navy" data-id="element-1812">
                  €{p.total}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden" data-id="element-1813">
                <div className="bg-coral h-2 rounded-full transition-all" style={{
              width: `${Math.round(p.total / maxPayout * 100)}%`
            }} data-id="element-1814" />
              </div>
            </div>)}
        </CardContent>
      </Card>

      {/* Per intervention */}
      <h2 className="text-sm font-semibold text-navy flex items-center gap-2 mb-3" data-id="element-1815">
        <Home size={16} className="text-coral" data-id="element-1816" />
        Interventi completati
      </h2>
      <Card data-id="element-1817">
        <CardContent noPadding data-id="element-1818">
          <ul className="divide-y divide-border" data-id="element-1819">
            {completed.map((r, idx) => <li key={idx} className="flex items-center justify-between gap-3 px-4 py-3" data-id="element-1820">
                <div className="min-w-0" data-id="element-1821">
                  <div className="text-sm font-medium text-foreground truncate" data-id="element-1822">
                    {r.apartmentName}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5" data-id="element-1823">
                    <User size={11} data-id="element-1824" />
                    {r.cleaner}
                    <span data-id="element-1825">·</span>
                    <Clock size={11} data-id="element-1826" />
                    {r.date}
                  </div>
                </div>
                <span className="text-sm font-semibold text-navy flex-shrink-0" data-id="element-1827">
                  €{r.earning}
                </span>
              </li>)}
          </ul>
        </CardContent>
      </Card>
    </MobileLayout>;
}